import React, { useState, useEffect } from 'react';
import { Box, Modal, TextField, Switch } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';

// ============================================
// Styled Components
// ============================================
const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '614px',
  backgroundColor: '#fff',
  borderRadius: '8px',
  overflow: 'hidden',
  outline: 'none',
});

const ModalHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 24px',
  height: '48px',
  backgroundColor: '#262626',
});

const ModalTitle = styled('p')({
  fontSize: '16px',
  fontWeight: 600,
  color: '#fff',
  margin: 0,
});

const CloseButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  cursor: 'pointer',
  color: '#fff',
  '&:hover': {
    opacity: 0.8,
  },
});

const ModalContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px',
});

const FormRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  height: '44px',
  width: '550px',
});

const FormLabel = styled('p')({
  fontSize: '18px',
  fontWeight: 600,
  color: '#262626',
  width: '98px',
  margin: 0,
  flexShrink: 0,
});

const StyledTextField = styled(TextField)({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    height: '44px',
    borderRadius: '4px',
    '& fieldset': {
      borderColor: '#b3b3b3',
    },
    '&:hover fieldset': {
      borderColor: '#0F7F85',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0F7F85',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#262626',
    '&::placeholder': {
      color: '#a6a6a6',
      opacity: 1,
    },
  },
});

const CategoryValue = styled('p')({
  flex: 1,
  fontSize: '14px',
  fontWeight: 500,
  color: '#262626',
  margin: 0,
});

const UseYnSwitch = styled(Switch)({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#0F7F85',
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#0F7F85',
  },
});

const UseYnStatusText = styled('p')(({ isActive }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: isActive ? '#0F7F85' : '#a6a6a6',
  margin: 0,
  marginLeft: '4px',
}));

const ButtonGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

const CancelButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '40px',
  padding: '12px 32px',
  borderRadius: '8px',
  backgroundColor: '#d9d9d9',
  cursor: 'pointer',
  '& p': {
    fontSize: '16px',
    fontWeight: 600,
    color: '#404040',
    margin: 0,
    whiteSpace: 'nowrap',
  },
  '&:hover': {
    backgroundColor: '#c9c9c9',
  },
});

const SaveButton = styled(Box, {
  shouldForwardProp: prop => prop !== 'disabled',
})(({ disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '40px',
  width: '200px',
  padding: '12px 24px',
  borderRadius: '8px',
  backgroundColor: disabled ? '#d9d9d9' : '#0f7f85',
  cursor: disabled ? 'not-allowed' : 'pointer',
  '& p': {
    fontSize: '16px',
    fontWeight: 600,
    color: disabled ? '#a6a6a6' : '#fff',
    margin: 0,
    whiteSpace: 'nowrap',
  },
  '&:hover': {
    backgroundColor: disabled ? '#d9d9d9' : '#0d6a6f',
  },
}));

// ============================================
// Constants
// ============================================
// 카테고리는 좌측 카드에서 넣고 뺀다. 여기선 표시만
const CATEGORY_LABELS = {
  LIGHT: '조명',
  AIRCON: '냉난방',
  HEATING: '냉난방',
  DISPLAY: '전광판',
  EQUIPMENT: '운동기구',
  ETC: '기타',
};

// ============================================
// Component
// ============================================
const LightNameEditModal = ({ open, onClose, onSave, lightData, isLoading }) => {
  const { t } = useTranslation();
  const [lightName, setLightName] = useState('');
  const [useYn, setUseYn] = useState('Y');
  const categoryLabel = CATEGORY_LABELS[lightData?.category] || lightData?.category || '전원';

  // lightData가 변경되면 초기값 설정
  useEffect(() => {
    if (open && lightData) {
      setLightName(lightData.relayName || '');
      setUseYn(lightData.useYn || 'Y');
    }
  }, [open, lightData]);

  const handleClose = () => {
    setLightName('');
    setUseYn('Y');
    onClose();
  };

  const handleSave = () => {
    if (!lightName.trim()) return;
    onSave({
      relayChannel: lightData.relayChannel,
      relayName: lightName.trim(),
      useYn,
    });
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{t('장치명 수정')}</ModalTitle>
          <CloseButton onClick={handleClose}>
            <CloseIcon style={{ fontSize: 24 }} />
          </CloseButton>
        </ModalHeader>
        <ModalContent>
          <FormRow>
            <FormLabel>{t('장치명')}</FormLabel>
            <StyledTextField
              value={lightName}
              onChange={e => setLightName(e.target.value)}
              placeholder={t('장치명을 입력해 주세요.')}
              fullWidth
            />
          </FormRow>
          <FormRow>
            <FormLabel>{t('카테고리')}</FormLabel>
            <CategoryValue>{t(categoryLabel)}</CategoryValue>
          </FormRow>
          <FormRow>
            <FormLabel>{t('사용 여부')}</FormLabel>
            <Box style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <UseYnSwitch checked={useYn === 'Y'} onChange={e => setUseYn(e.target.checked ? 'Y' : 'N')} />
              <UseYnStatusText isActive={useYn === 'Y'}>{useYn === 'Y' ? t('사용') : t('미사용')}</UseYnStatusText>
            </Box>
          </FormRow>
          <ButtonGroup>
            <CancelButton onClick={handleClose}>
              <p>{t('취소')}</p>
            </CancelButton>
            <SaveButton disabled={!lightName.trim() || isLoading} onClick={handleSave}>
              <p>{isLoading ? t('저장 중...') : t('저장')}</p>
            </SaveButton>
          </ButtonGroup>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default LightNameEditModal;
